// @flow

import React, { Component } from 'react';

import { Link, History } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { login, setAuthForm, setFormErrors, unsetError, checkAuth } from '../../actions/auth';
import { fieldError, genericFormError } from '../../store/reducers/auth';
import AuthLayout from '../layouts/AuthLayout';
import AuthError from '../shared/AuthError';
import AuthInput from '../shared/AuthInput';
import PasswordToggle from '../shared/PasswordToggle';
import Button from '../shared/Button';
import type { AuthState, FormError } from '../../store/reducers/auth';
import validate from '../../validators/login';
import Text from '../shared/Typography';
import Signup from './signup';
import { getRouteTFunction } from '../../i18n';

type State = {
  showPassword: boolean,
  rememberMe: boolean
};

type AuthForm = {
  email?: string,
  password?: string
}

type Props = {
  history: History,
  auth: AuthState,
  location: any,
  formError: FormError,
  login: (email: string, password: string, tfa_token: ?string, remember: boolean) => Promise<void>,
  loginOverride: (email: string, password: string, tfa_token: ?string, remember: boolean) => Promise<void>,
  signupOverride: (email: string, password: string) => Promise<void>,
  checkAuth: () => void,
  unsetError: (field: string) => void,
  setFormErrors: (errors: FormError[]) => void,
  fieldError: (field: string) => FormError,
  setAuthForm: (form: AuthForm) => void,
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void,
  loginHeading: ?string,
  signupHeading: ?string,
  noSubHeading: ?boolean,
  tab: ?string
} & WithTranslation

class Login extends Component<Props, State> {
  state: State = {
    showPassword: false,
    rememberMe: false,
    tab: this.props.tab
  }

  props: Props

  componentWillMount() {
    this.initTab(this.props.location);
    this.resetForm();
    // Commenting the below line because it is causing this page to redirect to /login if the user is not authenticated
    // and we don't want to redirect, ex. in case we are within an integration workflow
    // this.props.checkAuth();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location !== this.props.location) {
      this.initTab(nextProps.location);
      this.resetForm();
    }
  }

  initTab(location) {
    if (!location || !location.pathname) return;
    if (location.pathname.match(/signup/)) {
      this.setState({ tab: 'signup' });
    } else if (location.pathname.match(/login/)) {
      this.setState({ tab: 'login' });
    }
  }

  resetForm() {
    this.props.setAuthForm({
      password: ''
    });
    this.props.setFormErrors([]);
  }

  onSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const localErrors = validate({
      email: this.props.auth.email,
      password: this.props.auth.password
    });
    this.props.setFormErrors(localErrors);
    if (localErrors.length < 1) {
      if (this.props.loginOverride) {
        this.props.loginOverride(
          this.props.auth.email,
          this.props.auth.password,
          this.props.auth.tfa_token,
          this.state.rememberMe
        );
      } else {
        this.props.login(
          this.props.auth.email,
          this.props.auth.password,
          this.props.auth.tfa_token,
          this.state.rememberMe
        );
      }
    }
  }

  onChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    this.props.unsetError(event.currentTarget.name);
    this.props.setAuthForm({
      [event.currentTarget.name]: event.currentTarget.value || ''
    });
  }

  togglePassword = () => {
    this.setState({
      showPassword: !this.state.showPassword
    });
  }

  render() {
    const { auth, loginHeading, signupHeading, noSubHeading, t } = this.props;
    const routesTFunction = getRouteTFunction();
    if (auth.authenticated) {
      return null;
    }
    const { tab } = this.state;
    return (
      <AuthLayout className='login'>
        <div className='auth-page__form'>
          <div className='auth-tabs'>
            <button
              className={`auth-tabs__tab ${this.state.tab === 'signup' && 'is-active'}`}
              onClick={() => this.setState({ tab: 'signup' })}
            >
              {t('create_account')}
            </button>
            <button
              className={`auth-tabs__tab ${!this.state.tab || this.state.tab === 'login' ? 'is-active' : ''}`}
              onClick={() => this.setState({ tab: 'login' })}
            >
              {t('log_in_account')}
            </button>
          </div>
          {(!tab || tab === 'login') &&
          <React.Fragment>
            <div className='auth-page__header'>
              <Text h1>{loginHeading || t('greeting')}</Text>
              {!noSubHeading && <Text paragraph>{t('log_in_records')}</Text>}
            </div>
            <form onSubmit={this.onSubmit} className='is-functional auth-form--login' noValidate>
              {auth.tfa_challenge &&
                <AuthInput
                  value={this.props.auth.tfa_token || ''}
                  onChange={this.onChange}
                  name='tfa_token'
                  type='text'
                  label={t('two_factor_auth')}
                  error={this.props.fieldError('tfa_token') && <AuthError error={this.props.fieldError('tfa_token')} />}
                />
              }
              {!auth.tfa_challenge &&
                <div>
                  <div className='auth-form__row'>
                    <AuthInput
                      value={this.props.auth.email}
                      onChange={this.onChange}
                      name='email'
                      type='email'
                      label={t('email')}
                      error={this.props.fieldError('email') && <AuthError error={this.props.fieldError('email')} />}
                    />
                  </div>
                  <div className='auth-form__row'>
                    <AuthInput
                      value={this.props.auth.password}
                      onChange={this.onChange}
                      error={this.props.fieldError('password') &&
                        <AuthError error={this.props.fieldError('password')} />
                      }
                      label={t('password')}
                      type={this.state.showPassword ? 'text' : 'password'}
                      name='password'
                    />
                    <PasswordToggle checked={this.state.showPassword} onChange={this.togglePassword} />
                  </div>
                </div>
              }
              <div className='auth-form__footer'>
                {/* <div className='auth-form__remember'>
                <label htmlFor='remember_me'>
                  <input
                    type='checkbox'
                    name='remember_me'
                    id='remember_me'
                    checked={this.state.rememberMe}
                    onChange={() => {
                      this.setState({
                        rememberMe: !this.state.rememberMe
                      });
                  }} />
                  <span>Keep me logged in</span>
                </label>
              </div> */}
                {!auth.tfa_challenge &&
                  <div className='auth-form__forgot-pw'>
                    <Link className='is-link' to={routesTFunction('redirectPaths./forgot-password')}>
                      {t('forgot_password')}
                    </Link>
                  </div>
                }
              </div>
              <div className='auth-form__errors'>
                <AuthError error={this.props.formError} />
              </div>
              <Button
                formButton
                label={t('log_in')}
                loading={this.props.auth.loading}
              />
            </form>
          </React.Fragment>
          }
          {tab === 'signup' &&
            <Signup
              signupOverride={this.props.signupOverride}
              heading={signupHeading}
              noSubHeading={noSubHeading}
            />
          }
        </div>
      </AuthLayout>
    );
  }
}

const mapStateToProps = (appState: { auth: AuthState }) => ({
  auth: appState.auth,
  fieldError: (field) => fieldError(appState.auth, field),
  formError: genericFormError(appState.auth)
});

export default withTranslation('login')(
  connect(mapStateToProps, {
    login,
    setAuthForm,
    setFormErrors,
    unsetError,
    checkAuth
  })(Login)
);



// WEBPACK FOOTER //
// ./src/components/pages/login.js